import React from 'react';
import { Pressable, StyleSheet, Text, ViewStyle, TextStyle } from 'react-native';
import theme from '../theme';

type Tone = 'primary' | 'secondary' | 'success' | 'error';

type Props = {
  title: string;
  onPress: () => void;
  disabled?: boolean;
  tone?: Tone;
  variant?: 'solid' | 'outline';
  style?: ViewStyle;
  textStyle?: TextStyle;
};

const toneColor: Record<Tone, string> = {
  primary: theme.colors.primary,
  secondary: theme.colors.secondary,
  success: theme.colors.success,
  error: theme.colors.error,
};

export default function AppButton({
  title,
  onPress,
  disabled = false,
  tone = 'primary',
  variant = 'solid',
  style,
  textStyle,
}: Props) {
  const c = toneColor[tone];

  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      disabled={disabled}
      style={({ pressed }) => [
        styles.base,
        variant === 'solid'
          ? { backgroundColor: c, borderColor: theme.colors.border }
          : { backgroundColor: theme.colors.surface, borderColor: c },
        pressed ? styles.pressed : styles.raised,
        disabled ? styles.disabled : null,
        style,
      ]}
    >
      {({ pressed }) => (
        <Text
          style={[
            styles.text,
            variant === 'solid' ? { color: theme.colors.surface } : { color: c },
            pressed ? styles.textPressed : null,
            disabled ? styles.textDisabled : null,
            textStyle,
          ]}
        >
          {title}
        </Text>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  base: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: theme.radii.lg,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 42,
  },
  raised: {
    transform: [{ translateY: 0 }],
    shadowColor: theme.colors.textPrimary,
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.18,
    shadowRadius: 4,
    elevation: 3,
  },
  pressed: {
    transform: [{ translateY: 1 }],
    shadowColor: theme.colors.textPrimary,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.12,
    shadowRadius: 2,
    elevation: 1,
  },
  disabled: {
    opacity: 0.55,
  },
  text: {
    fontWeight: '800',
    fontSize: 14,
  },
  textPressed: {
    opacity: 0.95,
  },
  textDisabled: {
    opacity: 0.9,
  },
});
